/**
 * Symbol Mapper Utility
 *
 * Maps between Q.AI pair symbols and QuantX _ct symbols.
 *
 * Q.AI API returns pair symbols like: "EURUSD/GBPUSD", "GOLD/SILVER"
 * QuantX API expects _ct symbols like: "EURUSD_ct", "XAUUSD_ct"
 *
 * @version 1.0.0
 * @phase Phase 1B - Enhanced Signal Grid Fix
 */

/**
 * Mapping from Q.AI pair symbols to QuantX _ct symbols
 *
 * COMPREHENSIVE MAPPING: Covers all variations from signal board API
 * NOTE: Some pairs may map to the same QuantX symbol (e.g., gold pairs)
 * The first symbol in the pair is typically the primary instrument
 */
const PAIR_TO_QUANTX_MAP = {
  // ═══════════════════════════════════════════════════════════════════════════
  // GOLD VARIATIONS - Each maps to its own QuantX symbol
  // ═══════════════════════════════════════════════════════════════════════════
  'GOLD/SILVER': 'XAUUSD_ct',
  'GOLD': 'XAUUSD_ct',
  'XAUUSD/XAUEUR': 'XAUUSD_ct',   // Cross-pair: primary is XAUUSD
  'XAUEUR/XAUUSD': 'XAUEUR_ct',   // Cross-pair: primary is XAUEUR
  'XAUUSD': 'XAUUSD_ct',
  'XAUEUR': 'XAUEUR_ct',          // EUR-denominated gold has its own symbol
  'XAUUSD_ct': 'XAUUSD_ct',       // Already in QuantX format
  'XAUEUR_ct': 'XAUEUR_ct',       // Already in QuantX format

  // ═══════════════════════════════════════════════════════════════════════════
  // SILVER VARIATIONS - Each maps to its own QuantX symbol
  // ═══════════════════════════════════════════════════════════════════════════
  'SILVER': 'XAGUSD_ct',
  'XAGUSD/XAGEUR': 'XAGUSD_ct',   // Cross-pair: primary is XAGUSD
  'XAGEUR/XAGUSD': 'XAGEUR_ct',   // Cross-pair: primary is XAGEUR
  'XAGUSD': 'XAGUSD_ct',
  'XAGEUR': 'XAGEUR_ct',          // EUR-denominated silver has its own symbol
  'XAGUSD_ct': 'XAGUSD_ct',       // Already in QuantX format
  'XAGEUR_ct': 'XAGEUR_ct',       // Already in QuantX format

  // ═══════════════════════════════════════════════════════════════════════════
  // FOREX PAIRS - Major currency pairs
  // ═══════════════════════════════════════════════════════════════════════════
  'EURUSD/GBPUSD': 'EURUSD_ct',
  'GBPUSD/EURUSD': 'GBPUSD_ct',
  'EURUSD': 'EURUSD_ct',
  'GBPUSD': 'GBPUSD_ct',
  'USDJPY/EURJPY': 'USDJPY_ct',
  'EURJPY/USDJPY': 'EURJPY_ct',
  'USDJPY': 'USDJPY_ct',
  'EURJPY': 'EURJPY_ct',
  'AUDUSD': 'AUDUSD_ct',
  'NZDUSD': 'NZDUSD_ct',
  'USDCAD': 'USDCAD_ct',
  'USDCHF': 'USDCHF_ct',

  // ═══════════════════════════════════════════════════════════════════════════
  // CRYPTO PAIRS - Bitcoin and Ethereum
  // ═══════════════════════════════════════════════════════════════════════════
  'BTCUSD/ETHUSD': 'BTCUSD_ct',
  'ETHUSD/BTCUSD': 'ETHUSD_ct',
  'BTCUSD': 'BTCUSD_ct',
  'ETHUSD': 'ETHUSD_ct',
  'BTC/USD': 'BTCUSD_ct',
  'ETH/USD': 'ETHUSD_ct',
};

/**
 * Reverse mapping from QuantX _ct symbols to display names
 * AUDIT: Must include ALL Market Watch symbols (8 total)
 */
const QUANTX_TO_DISPLAY_MAP = {
  // Gold
  'XAUUSD_ct': 'XAUUSD',
  'XAUEUR_ct': 'XAUEUR',
  // Silver
  'XAGUSD_ct': 'XAGUSD',
  'XAGEUR_ct': 'XAGEUR',
  // Forex
  'EURUSD_ct': 'EURUSD',
  'GBPUSD_ct': 'GBPUSD',
  'USDJPY_ct': 'USDJPY',
  'EURJPY_ct': 'EURJPY',
  'AUDUSD_ct': 'AUDUSD',
  'NZDUSD_ct': 'NZDUSD',
  'USDCAD_ct': 'USDCAD',
  'USDCHF_ct': 'USDCHF',
  // Crypto
  'BTCUSD_ct': 'BTCUSD',
  'ETHUSD_ct': 'ETHUSD',
};

/**
 * Convert Q.AI pair symbol to QuantX _ct symbol
 *
 * @param {string} pairSymbol - Q.AI pair symbol (e.g., "EURUSD/GBPUSD")
 * @returns {string|null} - QuantX symbol (e.g., "EURUSD_ct") or null if not found
 *
 * @example
 * pairToQuantX("GOLD/SILVER") // => "XAUUSD_ct"
 * pairToQuantX("EURUSD/GBPUSD") // => "EURUSD_ct"
 */
export const pairToQuantX = (pairSymbol) => {
  if (!pairSymbol) return null;

  // Direct lookup
  if (PAIR_TO_QUANTX_MAP[pairSymbol]) {
    return PAIR_TO_QUANTX_MAP[pairSymbol];
  }

  // Try extracting first symbol from pair
  if (pairSymbol.includes('/')) {
    const firstSymbol = pairSymbol.split('/')[0];
    // Check if it's a gold/silver reference
    if (firstSymbol === 'GOLD') return 'XAUUSD_ct';
    if (firstSymbol === 'SILVER') return 'XAGUSD_ct';
    // Otherwise append _ct
    return `${firstSymbol}_ct`;
  }

  // If already has _ct suffix, return as-is
  if (pairSymbol.endsWith('_ct')) {
    return pairSymbol;
  }

  // Fallback: append _ct
  return `${pairSymbol}_ct`;
};

/**
 * Convert QuantX _ct symbol to display name (without suffix)
 *
 * @param {string} quantxSymbol - QuantX symbol (e.g., "EURUSD_ct")
 * @returns {string} - Display name (e.g., "EURUSD")
 *
 * @example
 * quantXToDisplay("EURUSD_ct") // => "EURUSD"
 * quantXToDisplay("XAUUSD_ct") // => "XAUUSD"
 */
export const quantXToDisplay = (quantxSymbol) => {
  if (!quantxSymbol) return '';

  // Direct lookup
  if (QUANTX_TO_DISPLAY_MAP[quantxSymbol]) {
    return QUANTX_TO_DISPLAY_MAP[quantxSymbol];
  }

  // Strip _ct suffix
  return quantxSymbol.replace('_ct', '');
};

/**
 * Extract the primary symbol from a Q.AI pair symbol
 *
 * @param {string} pairSymbol - Q.AI pair symbol (e.g., "EURUSD/GBPUSD")
 * @returns {string} - Primary symbol (e.g., "EURUSD")
 *
 * @example
 * extractPrimarySymbol("GOLD/SILVER") // => "GOLD"
 * extractPrimarySymbol("EURUSD/GBPUSD") // => "EURUSD"
 */
export const extractPrimarySymbol = (pairSymbol) => {
  if (!pairSymbol) return '';

  if (pairSymbol.includes('/')) {
    return pairSymbol.split('/')[0];
  }

  // Strip _ct if present
  return pairSymbol.replace('_ct', '');
};

/**
 * Check if a Q.AI pair symbol matches a QuantX symbol
 *
 * @param {string} pairSymbol - Q.AI pair symbol (e.g., "GOLD/SILVER")
 * @param {string} quantxSymbol - QuantX symbol (e.g., "XAUUSD_ct")
 * @returns {boolean} - True if they refer to the same instrument
 *
 * @example
 * symbolsMatch("GOLD/SILVER", "XAUUSD_ct") // => true
 * symbolsMatch("EURUSD/GBPUSD", "EURUSD_ct") // => true
 */
export const symbolsMatch = (pairSymbol, compareSymbol) => {
  if (!pairSymbol || !compareSymbol) return false;

  // Direct match (same format - pair to pair or quantx to quantx)
  if (pairSymbol === compareSymbol) return true;

  // Normalize both symbols to QuantX format and compare
  const mappedPair = pairToQuantX(pairSymbol);
  const mappedCompare = pairToQuantX(compareSymbol);

  return mappedPair === mappedCompare;
};

/**
 * Get all QuantX symbols for the given Q.AI pair symbols
 *
 * @param {string[]} pairSymbols - Array of Q.AI pair symbols
 * @returns {string[]} - Unique array of QuantX symbols
 */
export const getQuantXSymbols = (pairSymbols) => {
  if (!pairSymbols || !Array.isArray(pairSymbols)) return [];

  const quantxSymbols = pairSymbols
    .map(pair => pairToQuantX(pair))
    .filter(Boolean);

  // Return unique symbols
  return [...new Set(quantxSymbols)];
};

/**
 * Get display name for a symbol (user-friendly format)
 *
 * @param {string} symbol - Any symbol format
 * @returns {string} - Display-friendly name
 *
 * @example
 * getDisplaySymbol("XAUUSD_ct") // => "XAUUSD"
 * getDisplaySymbol("GOLD/SILVER") // => "GOLD/SILVER"
 */
export const getDisplaySymbol = (symbol) => {
  if (!symbol) return '';

  // If it's a QuantX symbol, convert to display format
  if (symbol.endsWith('_ct')) {
    return quantXToDisplay(symbol);
  }

  // Otherwise return as-is (pair symbols are already user-friendly)
  return symbol;
};

/**
 * Get compact symbol name (short format for space-constrained UI)
 *
 * @param {string} symbol - Any symbol format
 * @returns {string} - Compact name (primary symbol only)
 *
 * @example
 * getCompactSymbol("GOLD/SILVER") // => "GOLD"
 * getCompactSymbol("EURUSD/GBPUSD") // => "EURUSD"
 * getCompactSymbol("XAUUSD_ct") // => "XAUUSD"
 */
export const getCompactSymbol = (symbol) => {
  if (!symbol) return '';

  // Extract primary symbol (works for pairs, quantx, and plain symbols)
  return extractPrimarySymbol(symbol);
};

const symbolMapper = {
  pairToQuantX,
  quantXToDisplay,
  extractPrimarySymbol,
  symbolsMatch,
  getQuantXSymbols,
  getDisplaySymbol,
  getCompactSymbol,
};

export default symbolMapper;
